export const DEFAULT_BOX_ACTIVATION_THRESHOLD = 4;
export const DEFAULT_LASSO_SAMPLE_SPACING = 2;
export const DEFAULT_LASSO_RDP_TOLERANCE = 1.5;
export const DEFAULT_LASSO_MINIMUM_AREA = 16;
export const SCREEN_REGION_EPSILON = 1e-9;
export function assertFiniteScreenPoint(point, label = "Screen point") {
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
        throw new RangeError(`${label} must contain finite x/y coordinates.`);
    }
}
export function screenDistanceSquared(left, right) {
    assertFiniteScreenPoint(left, "Left screen point");
    assertFiniteScreenPoint(right, "Right screen point");
    const dx = right.x - left.x;
    const dy = right.y - left.y;
    return dx * dx + dy * dy;
}
export function normalizeScreenBounds(start, end) {
    assertFiniteScreenPoint(start, "Box start");
    assertFiniteScreenPoint(end, "Box end");
    return {
        minX: Math.min(start.x, end.x),
        minY: Math.min(start.y, end.y),
        maxX: Math.max(start.x, end.x),
        maxY: Math.max(start.y, end.y),
    };
}
export function isScreenDragActive(start, current, threshold = DEFAULT_BOX_ACTIVATION_THRESHOLD) {
    assertNonNegativeFinite(threshold, "Screen drag threshold");
    return screenDistanceSquared(start, current) >= threshold * threshold;
}
export function screenBoundsHasPositiveArea(bounds) {
    assertFiniteScreenBounds(bounds);
    return bounds.maxX > bounds.minX && bounds.maxY > bounds.minY;
}
export function screenBoundsToRing(bounds) {
    assertFiniteScreenBounds(bounds);
    const topLeft = { x: bounds.minX, y: bounds.minY };
    const topRight = { x: bounds.maxX, y: bounds.minY };
    const bottomRight = { x: bounds.maxX, y: bounds.maxY };
    const bottomLeft = { x: bounds.minX, y: bounds.maxY };
    return [topLeft, topRight, bottomRight, bottomLeft, topLeft];
}
export function appendLassoSample(points, candidate, spacing = DEFAULT_LASSO_SAMPLE_SPACING) {
    assertNonNegativeFinite(spacing, "Lasso sample spacing");
    assertFiniteScreenPoint(candidate, "Lasso sample");
    const last = points.at(-1);
    if (last === undefined)
        return [{ ...candidate }];
    assertFiniteScreenPoint(last, "Existing lasso sample");
    if (screenDistanceSquared(last, candidate) < spacing * spacing) {
        return [...points];
    }
    return [...points, { ...candidate }];
}
export function removeConsecutiveDuplicateScreenPoints(points, epsilon = SCREEN_REGION_EPSILON) {
    assertNonNegativeFinite(epsilon, "Screen equality epsilon");
    const result = [];
    for (const [index, point] of points.entries()) {
        assertFiniteScreenPoint(point, `Screen point ${index}`);
        const previous = result.at(-1);
        if (previous === undefined || !sameScreenPoint(previous, point, epsilon)) {
            result.push({ ...point });
        }
    }
    return result;
}
export function signedScreenRingArea(points) {
    const ring = normalizeOpenRing(points, SCREEN_REGION_EPSILON);
    if (ring.length < 3)
        return 0;
    let twiceArea = 0;
    for (let index = 0; index < ring.length; index += 1) {
        const current = ring[index];
        const next = ring[(index + 1) % ring.length];
        twiceArea += current.x * next.y - next.x * current.y;
    }
    return twiceArea / 2;
}
export function simplifyScreenPath(points, tolerance = DEFAULT_LASSO_RDP_TOLERANCE) {
    assertNonNegativeFinite(tolerance, "RDP tolerance");
    const normalized = removeConsecutiveDuplicateScreenPoints(points);
    if (normalized.length <= 2)
        return normalized;
    return simplifyScreenPathRecursive(normalized, tolerance);
}
export function validateAndSimplifyScreenLasso(rawPoints, options = {}) {
    const minimumDistinctPoints = options.minimumDistinctPoints ?? 3;
    const minimumArea = options.minimumArea ?? DEFAULT_LASSO_MINIMUM_AREA;
    const simplifyTolerance = options.simplifyTolerance ?? DEFAULT_LASSO_RDP_TOLERANCE;
    const epsilon = options.epsilon ?? SCREEN_REGION_EPSILON;
    if (!Number.isInteger(minimumDistinctPoints) || minimumDistinctPoints < 3) {
        throw new RangeError("Lasso minimumDistinctPoints must be an integer >= 3.");
    }
    assertNonNegativeFinite(minimumArea, "Lasso minimum area");
    assertNonNegativeFinite(simplifyTolerance, "Lasso simplify tolerance");
    assertNonNegativeFinite(epsilon, "Lasso epsilon");
    const raw = normalizeOpenRing(rawPoints, epsilon);
    const rawMinimumIssue = minimumLassoIssue(raw, minimumDistinctPoints, minimumArea);
    if (rawMinimumIssue !== undefined) {
        return invalidLasso(raw, rawMinimumIssue);
    }
    if (!isSimpleScreenRing(raw, epsilon)) {
        return invalidLasso(raw, selfIntersectionIssue());
    }
    const simplified = normalizeOpenRing(simplifyScreenPath(raw, simplifyTolerance), epsilon);
    const simplifiedMinimumIssue = minimumLassoIssue(simplified, minimumDistinctPoints, minimumArea);
    if (simplifiedMinimumIssue !== undefined) {
        return invalidLasso(simplified, simplifiedMinimumIssue);
    }
    if (!isSimpleScreenRing(simplified, epsilon)) {
        return invalidLasso(simplified, selfIntersectionIssue());
    }
    const bounds = screenPointsBounds(simplified);
    const area = Math.abs(signedScreenRingArea(simplified));
    const points = copyScreenPoints(simplified);
    const ring = [...copyScreenPoints(simplified), { ...simplified[0] }];
    return {
        valid: true,
        points,
        ring,
        bounds,
        area,
    };
}
export function isSimpleScreenRing(points, epsilon = SCREEN_REGION_EPSILON) {
    assertNonNegativeFinite(epsilon, "Screen ring epsilon");
    const ring = normalizeOpenRing(points, epsilon);
    if (ring.length < 3)
        return false;
    for (let left = 0; left < ring.length; left += 1) {
        for (let right = left + 1; right < ring.length; right += 1) {
            if (sameScreenPoint(ring[left], ring[right], epsilon))
                return false;
        }
    }
    for (let left = 0; left < ring.length; left += 1) {
        const leftStart = ring[left];
        const leftEnd = ring[(left + 1) % ring.length];
        if (sameScreenPoint(leftStart, leftEnd, epsilon))
            return false;
        for (let right = left + 1; right < ring.length; right += 1) {
            if (segmentsAreAdjacent(left, right, ring.length))
                continue;
            const rightStart = ring[right];
            const rightEnd = ring[(right + 1) % ring.length];
            if (screenSegmentsIntersect(leftStart, leftEnd, rightStart, rightEnd, epsilon)) {
                return false;
            }
        }
    }
    return true;
}
export function screenOrientation(first, second, third) {
    assertFiniteScreenPoint(first, "Orientation first point");
    assertFiniteScreenPoint(second, "Orientation second point");
    assertFiniteScreenPoint(third, "Orientation third point");
    return ((second.x - first.x) * (third.y - first.y) -
        (second.y - first.y) * (third.x - first.x));
}
export function isPointOnScreenSegment(point, start, end, epsilon = SCREEN_REGION_EPSILON) {
    assertNonNegativeFinite(epsilon, "Segment epsilon");
    if (Math.abs(screenOrientation(start, end, point)) > epsilon)
        return false;
    return (point.x >= Math.min(start.x, end.x) - epsilon &&
        point.x <= Math.max(start.x, end.x) + epsilon &&
        point.y >= Math.min(start.y, end.y) - epsilon &&
        point.y <= Math.max(start.y, end.y) + epsilon);
}
export function screenSegmentsIntersect(firstStart, firstEnd, secondStart, secondEnd, epsilon = SCREEN_REGION_EPSILON) {
    assertNonNegativeFinite(epsilon, "Segment intersection epsilon");
    const firstSecondStart = signWithEpsilon(screenOrientation(firstStart, firstEnd, secondStart), epsilon);
    const firstSecondEnd = signWithEpsilon(screenOrientation(firstStart, firstEnd, secondEnd), epsilon);
    const secondFirstStart = signWithEpsilon(screenOrientation(secondStart, secondEnd, firstStart), epsilon);
    const secondFirstEnd = signWithEpsilon(screenOrientation(secondStart, secondEnd, firstEnd), epsilon);
    if (firstSecondStart * firstSecondEnd < 0 &&
        secondFirstStart * secondFirstEnd < 0) {
        return true;
    }
    if (firstSecondStart === 0 &&
        isPointOnScreenSegment(secondStart, firstStart, firstEnd, epsilon)) {
        return true;
    }
    if (firstSecondEnd === 0 &&
        isPointOnScreenSegment(secondEnd, firstStart, firstEnd, epsilon)) {
        return true;
    }
    if (secondFirstStart === 0 &&
        isPointOnScreenSegment(firstStart, secondStart, secondEnd, epsilon)) {
        return true;
    }
    return (secondFirstEnd === 0 &&
        isPointOnScreenSegment(firstEnd, secondStart, secondEnd, epsilon));
}
export function pointInScreenRing(point, points, epsilon = SCREEN_REGION_EPSILON) {
    assertFiniteScreenPoint(point, "Point-in-ring point");
    assertNonNegativeFinite(epsilon, "Point-in-ring epsilon");
    const ring = normalizeOpenRing(points, epsilon);
    if (ring.length < 3)
        return "outside";
    for (let index = 0; index < ring.length; index += 1) {
        const start = ring[index];
        const end = ring[(index + 1) % ring.length];
        if (isPointOnScreenSegment(point, start, end, epsilon)) {
            return "boundary";
        }
    }
    let inside = false;
    for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index, index += 1) {
        const currentPoint = ring[index];
        const previousPoint = ring[previous];
        const crosses = currentPoint.y > point.y !== previousPoint.y > point.y &&
            point.x <
                ((previousPoint.x - currentPoint.x) * (point.y - currentPoint.y)) /
                    (previousPoint.y - currentPoint.y) +
                    currentPoint.x;
        if (crosses)
            inside = !inside;
    }
    return inside ? "inside" : "outside";
}
export function pointInScreenPolygonFill(point, rings, epsilon = SCREEN_REGION_EPSILON) {
    const exterior = rings[0];
    if (exterior === undefined)
        return false;
    const exteriorResult = pointInScreenRing(point, exterior, epsilon);
    if (exteriorResult === "outside")
        return false;
    if (exteriorResult === "boundary")
        return true;
    for (let index = 1; index < rings.length; index += 1) {
        const hole = rings[index];
        if (hole === undefined)
            continue;
        const holeResult = pointInScreenRing(point, hole, epsilon);
        if (holeResult === "inside")
            return false;
        if (holeResult === "boundary")
            return true;
    }
    return true;
}
export function screenGeometryIntersectsRegion(geometry, regionPoints, epsilon = SCREEN_REGION_EPSILON) {
    const region = normalizeOpenRing(regionPoints, epsilon);
    if (region.length < 3)
        return false;
    switch (geometry.type) {
        case "Point":
            return pointInScreenRing(geometry.coordinates, region, epsilon) !== "outside";
        case "LineString":
            return screenLineIntersectsRegion(geometry.coordinates, region, epsilon);
        case "MultiLineString":
            return geometry.coordinates.some((line) => screenLineIntersectsRegion(line, region, epsilon));
        case "Polygon":
            return screenPolygonIntersectsRegion(geometry.coordinates, region, epsilon);
        case "MultiPolygon":
            return geometry.coordinates.some((polygon) => screenPolygonIntersectsRegion(polygon, region, epsilon));
    }
}
export function screenLineIntersectsRegion(line, regionPoints, epsilon = SCREEN_REGION_EPSILON) {
    const region = normalizeOpenRing(regionPoints, epsilon);
    for (const point of line) {
        assertFiniteScreenPoint(point, "Line screen point");
        if (pointInScreenRing(point, region, epsilon) !== "outside")
            return true;
    }
    for (let index = 0; index + 1 < line.length; index += 1) {
        const start = line[index];
        const end = line[index + 1];
        if (lineSegmentIntersectsRing(start, end, region, epsilon))
            return true;
    }
    return false;
}
export function screenPolygonIntersectsRegion(polygonRings, regionPoints, epsilon = SCREEN_REGION_EPSILON) {
    const region = normalizeOpenRing(regionPoints, epsilon);
    const exterior = polygonRings[0];
    if (region.length < 3 || exterior === undefined)
        return false;
    const normalizedRings = polygonRings.map((ring) => normalizeOpenRing(ring, epsilon));
    for (const ring of normalizedRings) {
        if (ringsBoundariesIntersect(ring, region, epsilon))
            return true;
    }
    for (const point of region) {
        if (pointInScreenPolygonFill(point, normalizedRings, epsilon))
            return true;
    }
    for (const point of normalizeOpenRing(exterior, epsilon)) {
        if (pointInScreenRing(point, region, epsilon) !== "outside")
            return true;
    }
    return false;
}
export function screenPointsBounds(points) {
    if (points.length === 0) {
        throw new RangeError("Cannot derive screen bounds from an empty point set.");
    }
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    for (const [index, point] of points.entries()) {
        assertFiniteScreenPoint(point, `Screen bounds point ${index}`);
        minX = Math.min(minX, point.x);
        minY = Math.min(minY, point.y);
        maxX = Math.max(maxX, point.x);
        maxY = Math.max(maxY, point.y);
    }
    return { minX, minY, maxX, maxY };
}
function minimumLassoIssue(points, minimumDistinctPoints, minimumArea) {
    if (points.length < minimumDistinctPoints) {
        return {
            code: "SELECTION_REGION_LASSO_TOO_FEW_POINTS",
            message: `Lasso requires at least ${minimumDistinctPoints} distinct screen points.`,
        };
    }
    if (Math.abs(signedScreenRingArea(points)) < minimumArea) {
        return {
            code: "SELECTION_REGION_TOO_SMALL",
            message: `Lasso area must be at least ${minimumArea} CSS px².`,
        };
    }
    return undefined;
}
function selfIntersectionIssue() {
    return {
        code: "SELECTION_REGION_LASSO_SELF_INTERSECTS",
        message: "Lasso must be a simple ring without repeated, crossing, touching or overlapping non-adjacent edges.",
    };
}
function invalidLasso(points, rejection) {
    return {
        valid: false,
        points: copyScreenPoints(points),
        rejection,
    };
}
function normalizeOpenRing(points, epsilon) {
    const normalized = [
        ...removeConsecutiveDuplicateScreenPoints(points, epsilon),
    ];
    if (normalized.length > 1 &&
        sameScreenPoint(normalized[0], normalized.at(-1), epsilon)) {
        normalized.pop();
    }
    return normalized;
}
function simplifyScreenPathRecursive(points, tolerance) {
    if (points.length <= 2)
        return copyScreenPoints(points);
    const first = points[0];
    const last = points.at(-1);
    let maximumDistance = -1;
    let splitIndex = -1;
    for (let index = 1; index < points.length - 1; index += 1) {
        const distance = distanceToScreenSegment(points[index], first, last);
        if (distance > maximumDistance) {
            maximumDistance = distance;
            splitIndex = index;
        }
    }
    if (maximumDistance <= tolerance || splitIndex < 1) {
        return [{ ...first }, { ...last }];
    }
    const left = simplifyScreenPathRecursive(points.slice(0, splitIndex + 1), tolerance);
    const right = simplifyScreenPathRecursive(points.slice(splitIndex), tolerance);
    return [...left.slice(0, -1), ...right];
}
function distanceToScreenSegment(point, start, end) {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    if (dx === 0 && dy === 0)
        return Math.sqrt(screenDistanceSquared(point, start));
    const projection = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) /
        (dx * dx + dy * dy)));
    const projected = {
        x: start.x + projection * dx,
        y: start.y + projection * dy,
    };
    return Math.sqrt(screenDistanceSquared(point, projected));
}
function ringsBoundariesIntersect(left, right, epsilon) {
    for (let leftIndex = 0; leftIndex < left.length; leftIndex += 1) {
        const leftStart = left[leftIndex];
        const leftEnd = left[(leftIndex + 1) % left.length];
        if (lineSegmentIntersectsRing(leftStart, leftEnd, right, epsilon)) {
            return true;
        }
    }
    return false;
}
function lineSegmentIntersectsRing(start, end, ring, epsilon) {
    for (let index = 0; index < ring.length; index += 1) {
        const ringStart = ring[index];
        const ringEnd = ring[(index + 1) % ring.length];
        if (screenSegmentsIntersect(start, end, ringStart, ringEnd, epsilon)) {
            return true;
        }
    }
    return false;
}
function segmentsAreAdjacent(leftIndex, rightIndex, segmentCount) {
    return (rightIndex === leftIndex + 1 ||
        (leftIndex === 0 && rightIndex === segmentCount - 1));
}
function sameScreenPoint(left, right, epsilon) {
    return (Math.abs(left.x - right.x) <= epsilon &&
        Math.abs(left.y - right.y) <= epsilon);
}
function signWithEpsilon(value, epsilon) {
    if (Math.abs(value) <= epsilon)
        return 0;
    return value < 0 ? -1 : 1;
}
function assertFiniteScreenBounds(bounds) {
    for (const [name, value] of Object.entries(bounds)) {
        if (!Number.isFinite(value)) {
            throw new RangeError(`Screen bounds ${name} must be finite.`);
        }
    }
}
function assertNonNegativeFinite(value, label) {
    if (!Number.isFinite(value) || value < 0) {
        throw new RangeError(`${label} must be a finite non-negative number.`);
    }
}
function copyScreenPoints(points) {
    return points.map((point) => ({ ...point }));
}
//# sourceMappingURL=screen-region-selection.js.map